import React from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Collapse,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { getRequest, postRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL } from "Includes/urls";
import CompanyChecklistTab from './CompanyChecklistTab';
import VisitSummaryTab from '../BasicDetails/VisitSummaryTab';

function fmtRs(val) {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return val;
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const statusStyle = (val) => {
  const v = String(val || "Active");
  const styles = {
    Active: { bgcolor: "#ECFDF3", color: "#027A48", borderColor: "#A6F4C5" },
    "Low Engagement": { bgcolor: "#EFF8FF", color: "#175CD3", borderColor: "#B2DDFF" },
    "At Risk": { bgcolor: "#FFFAEB", color: "#B54708", borderColor: "#FEDF89" },
    Dead: { bgcolor: "#FEF3F2", color: "#B42318", borderColor: "#FECDCA" },
  };
  return styles[v] || { bgcolor: "#F2F4F7", color: "#344054", borderColor: "#D0D5DD" };
};

const SUMMARY_COLUMN_KEYS = [
  'institute_name',
  'school_address',
  'institute_poc',
  'attendance_used_percentage',
  'diary_used_percentage',
  'last_exam_used_percentage',
  'exam_marks_used_percentage',
];

const formatValue = (value) => {
  if (value === 'Never Used') {
    return <Typography color="error" variant="body2">{value}</Typography>;
  }
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatInvoiceStatus = (code) => {
  const map = {
    DRAFT: "Draft",
    ISSUED: "Issued",
    INVOICED: "Invoiced",
    PARTIAL_PAID: "Partial paid",
    PAID: "Paid",
  };
  return map[String(code || "").toUpperCase()] || code || "—";
};

const invoiceStatusChipSx = (code) => {
  const s = String(code || "").toUpperCase();
  const styles = {
    DRAFT: { bgcolor: "#F2F4F7", color: "#344054" },
    ISSUED: { bgcolor: "#EFF8FF", color: "#175CD3" },
    INVOICED: { bgcolor: "#F4F3FF", color: "#5925DC" },
    PARTIAL_PAID: { bgcolor: "#FFFAEB", color: "#B54708" },
    PAID: { bgcolor: "#ECFDF3", color: "#027A48" },
  };
  return styles[s] || { bgcolor: "#F2F4F7", color: "#344054" };
};

// 🎨 Colored Summary Cards
const SummaryCard = ({ title, value, color }) => (
  <Card
    sx={{
      borderRadius: 3,
      color: "#fff",
      background: color,
      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
    }}
  >
    <CardContent>
      <Typography variant="caption" sx={{ opacity: 0.8 }}>
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        ₹ {fmtRs(value)}
      </Typography>
    </CardContent>
  </Card>
);

export default function CompanyBillingDetails(props) {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [payload, setPayload] = React.useState(null);

  const [activeTab, setActiveTab] = React.useState(0);
  const [reports, setReports] = React.useState([]);
  const [companyResources, setCompanyResources] = React.useState([]);
  const [reportColumns, setReportColumns] = React.useState([]);
  const [reportTabs, setReportTabs] = React.useState([]);
  const [expandedRowId, setExpandedRowId] = React.useState(null);
  const [teamReports, setTeamReports] = React.useState([]);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createDialogMode, setCreateDialogMode] = React.useState("order"); // order | invoice | payment
  const [createDialogYear, setCreateDialogYear] = React.useState("");
  const [createDialogLoading, setCreateDialogLoading] = React.useState(false);
  const [setupStudentStrength, setSetupStudentStrength] = React.useState("");
  const [setupCoordinator, setSetupCoordinator] = React.useState("");
  const [setupStudentStrengthError, setSetupStudentStrengthError] = React.useState("");
  const [setupCoordinatorError, setSetupCoordinatorError] = React.useState("");
  const [coordinators, setCoordinators] = React.useState([]);
  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);
  const [downloadRow, setDownloadRow] = React.useState(null);
  const [downloadShowGst, setDownloadShowGst] = React.useState(true);
  const [downloadShowDiscount, setDownloadShowDiscount] = React.useState(true);
  const [downloadShowBillingSummary, setDownloadShowBillingSummary] = React.useState(false);
  const [downloadBillingSummary, setDownloadBillingSummary] = React.useState("");
  const [downloadLoading, setDownloadLoading] = React.useState(false);

  React.useEffect(() => {
    if (!companyId) {
      setError("Missing company id");
      setLoading(false);
      return;
    }

    setPayload(null);
    setReports([]);
    setLoading(true);
    const overviewUrl = `${GET_URL.company.api}${companyId}/billing-overview/`;
    const reportUrl = GET_URL.client_track_report.api;
    const teamReportUrl = GET_URL.client_track_company_updates.api;

    Promise.all([
      getRequest(overviewUrl, {}, props).catch(() => null),
      getRequest(reportUrl, { company: companyId, limit: 1000 }, props).catch(() => null),
      getRequest(teamReportUrl, { company_id: companyId, limit: 100 }, props).catch(() => null)
    ])
      .then(([res1, res2, res3]) => {
        if (res1?.status === 200) {
          setPayload(res1?.data?.data || null);
        } else if (res1) {
          setError(res1?.data?.Reason || "Failed to load overview");
        }

        if (res2?.status === 200) {
          setReports(res2?.data?.data || []);
          setReportColumns(res2?.data?.columns || []);
          setReportTabs(res2?.data?.tabs || []);
          setCompanyResources(res2?.data?.company_resource || null);
        }

        if (res3?.status === 200) {
          setTeamReports(Array.isArray(res3.data) ? res3.data : (res3.data?.data || []));
        }
      })
      .finally(() => setLoading(false));
  }, [companyId, props]);


  const company = payload?.company;
  const years = payload?.years || [];

  const selectedYearBlock = React.useMemo(
    () => years.find((b) => String(b.year?.id) === String(createDialogYear)) || null,
    [years, createDialogYear]
  );

  const needsBillingSetup =
    createDialogMode === "order" && createDialogYear && !selectedYearBlock?.has_setup;

  const fetchCoordinators = React.useCallback(() => {
    getRequest(GET_URL.user.api, { groups: 4 }, props).then((response) => {
      if (response?.status === 200) {
        setCoordinators(response.data?.data || response.data || []);
      }
    });
  }, [props]);

  const resetSetupFields = () => {
    setSetupStudentStrength("");
    setSetupCoordinator("");
    setSetupStudentStrengthError("");
    setSetupCoordinatorError("");
  };

  const validateBillingSetupFields = () => {
    let valid = true;
    setSetupStudentStrengthError("");
    setSetupCoordinatorError("");

    if (!setupStudentStrength || setupStudentStrength === "") {
      setSetupStudentStrengthError("Student strength is required");
      valid = false;
    } else {
      const ss = parseFloat(setupStudentStrength);
      if (
        Number.isNaN(ss) ||
        ss <= 0 ||
        ss > 9999 ||
        !Number.isInteger(ss)
      ) {
        setSetupStudentStrengthError("Enter a whole number between 1 and 9999");
        valid = false;
      }
    }

    if (!setupCoordinator) {
      setSetupCoordinatorError("Coordinator is required");
      valid = false;
    }

    return valid;
  };

  const createBillingSetupAndNavigate = () => {
    if (!validateBillingSetupFields()) return Promise.resolve(null);

    const payload = [
      {
        company: Number(companyId),
        year: createDialogYear,
        student_strength: parseInt(setupStudentStrength, 10),
        coordinator: Number(setupCoordinator),
      },
    ];

    return postRequest(POST_URL.company_billing_config.api, payload, props).then(
      (response) => {
        if (response?.status !== 200) return null;
        const created = response?.data?.data;
        const createdList = Array.isArray(created) ? created : created ? [created] : [];
        const matched = createdList.find(
          (item) => Number(item?.company) === Number(companyId)
        );
        return matched?.id || createdList[0]?.id || null;
      }
    );
  };

  const openCreateDialog = (mode) => {
    const defaultYear =
      years.find((b) => b.has_setup)?.year?.id || years[0]?.year?.id || "";
    setCreateDialogMode(mode);
    setCreateDialogYear(defaultYear);
    resetSetupFields();
    setCreateDialogOpen(true);
    if (mode === "order") {
      fetchCoordinators();
    }
  };

  const handleCreateDialogContinue = () => {
    if (!createDialogYear) {
      Swal.fire("Select year", "Please select an academic year first", "warning");
      return;
    }
    if (!companyId) return;

    if (createDialogMode === "payment") {
      setCreateDialogOpen(false);
      navigate(`/billing/payment?year=${createDialogYear}&company=${companyId}`);
      return;
    }

    if (createDialogMode === "order" && needsBillingSetup) {
      setCreateDialogLoading(true);
      createBillingSetupAndNavigate()
        .then((configId) => {
          if (!configId) {
            Swal.fire(
              "Billing setup failed",
              "Could not create billing setup. Please check the details and try again.",
              "error"
            );
            return;
          }
          setCreateDialogOpen(false);
          resetSetupFields();
          navigate(`/billing/company-setup?config_id=${configId}`);
        })
        .finally(() => setCreateDialogLoading(false));
      return;
    }

    setCreateDialogLoading(true);
    getRequest(
      GET_URL.company_billing_config.api,
      { company: companyId, year: createDialogYear, limit: 1, pageno: 1 },
      props
    )
      .then((res) => {
        const cfg = res?.data?.data?.[0];
        setCreateDialogOpen(false);
        if (createDialogMode === "invoice") {
          if (cfg?.id) {
            navigate(`/billing/issue-invoice/?config_id=${cfg.id}`);
          } else {
            Swal.fire(
              "Billing setup required",
              "Create a billing setup / order for this company and year before issuing an invoice.",
              "warning"
            ).then(() => {
              navigate("/billing/company-billing-config-add", {
                state: { agreementOrderFlow: { companyId: Number(companyId) } },
              });
            });
          }
          return;
        }
        // create order
        if (cfg?.id) {
          navigate(`/billing/company-setup?config_id=${cfg.id}`);
        } else {
          navigate("/billing/company-billing-config-add", {
            state: { agreementOrderFlow: { companyId: Number(companyId) } },
          });
        }
      })
      .finally(() => setCreateDialogLoading(false));
  };

  const dialogTitle =
    createDialogMode === "invoice"
      ? "Create Invoice"
      : createDialogMode === "payment"
        ? "Add Payment"
        : "Create Order";

  const dialogContinueLabel =
    createDialogMode === "payment"
      ? "Continue to Payment"
      : needsBillingSetup
        ? "Create Setup & Continue"
        : "Continue";

  const handlePrintInvoice = (inv, e) => {
    if (e) e.stopPropagation();
    if (!inv?.id) return;
    setDownloadRow(inv);
    setDownloadShowGst(true);
    setDownloadShowDiscount(true);
    setDownloadShowBillingSummary(false);
    setDownloadBillingSummary("");
    setDownloadDialogOpen(true);
  };

  const handleDownloadDialogClose = () => {
    if (downloadLoading) return;
    setDownloadDialogOpen(false);
    setDownloadRow(null);
  };

  const handleDownloadInvoiceSubmit = () => {
    if (!downloadRow?.id) return;
    setDownloadLoading(true);
    const url = `${GET_URL.issued_invoices.api}${downloadRow.id}/`;
    const reqProps = { ...props, responseType: "blob" };
    const requestParams = {
      generate_pdf: 1,
      show_gst: downloadShowGst ? 1 : 0,
      show_discount: downloadShowDiscount ? 1 : 0,
      show_billing_summary: downloadShowBillingSummary ? 1 : 0,
      ...(downloadShowBillingSummary && downloadBillingSummary.trim()
        ? { billing_summary: downloadBillingSummary.trim() }
        : {}),
    };

    getRequest(url, requestParams, reqProps)
      .then((response) => {
        if (response && response.status === 200) {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const fileURL = window.URL.createObjectURL(blob);
          const contentDisposition =
            response?.headers?.["content-disposition"] ||
            response?.headers?.["Content-Disposition"];
          const match =
            typeof contentDisposition === "string"
              ? contentDisposition.match(/filename="?([^"]+)"?/i)
              : null;
          const filename =
            match?.[1] ||
            `invoice_${downloadRow.invoice_number || downloadRow.id}.pdf`;
          const link = document.createElement("a");
          link.href = fileURL;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(link);
          setDownloadDialogOpen(false);
          setDownloadRow(null);
        }
      })
      .catch((error) => {
        console.error("Error downloading invoice PDF:", error);
        Swal.fire("Error", "Failed to download invoice PDF", "error");
      })
      .finally(() => setDownloadLoading(false));
  };

  const summaryColumns = React.useMemo(() => {
    const selectedColumns = (reportColumns || []).filter((item) =>
      SUMMARY_COLUMN_KEYS.includes(item.schemacolumn)
    );
    // Sort columns to match SUMMARY_COLUMN_KEYS order if possible
    selectedColumns.sort((a, b) => {
      return SUMMARY_COLUMN_KEYS.indexOf(a.schemacolumn) - SUMMARY_COLUMN_KEYS.indexOf(b.schemacolumn);
    });
    return selectedColumns.length
      ? selectedColumns
      : (reportColumns || []).slice(0, 6);
  }, [reportColumns]);

  const detailColumns = React.useMemo(() => {
    const summaryKeys = new Set(summaryColumns.map((item) => item.schemacolumn));
    return (reportColumns || []).filter((item) => !summaryKeys.has(item.schemacolumn));
  }, [reportColumns, summaryColumns]);
  console.log("companyResources:", companyResources);
  console.log("reports:", reports);
  console.log("Report field names:", reports.length > 0 ? Object.keys(reports[0]) : "No reports");

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* BACK BUTTON */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/company/list")}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && company && (
        <>
          {/* 🌈 HEADER */}
          <Card
            sx={{
              borderRadius: 4,
              mb: 3,
              color: "#fff",
              background:
                "blue",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {company.name}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Code: {company.code} · Domain: {company.domain || "—"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => openCreateDialog("order")}
                    sx={{ color: "#1d4ed8", fontWeight: 600 }}
                  >
                    Create Order
                  </Button>
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => openCreateDialog("invoice")}
                    sx={{ color: "#1d4ed8", fontWeight: 600 }}
                  >
                    Create Invoice
                  </Button>
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => openCreateDialog("payment")}
                    sx={{ color: "#1d4ed8", fontWeight: 600 }}
                  >
                    Add Payment
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={
                    company.payment_gateway_enabled
                      ? "Gateway Enabled"
                      : "Gateway Disabled"
                  }
                  sx={{
                    background: "#fff",
                    color: "#333",
                  }}
                />
                <Chip
                  label={company.is_active ? "Active" : "Inactive"}
                  sx={{
                    background: company.is_active ? "#22c55e" : "#ef4444",
                    color: "#fff",
                  }}
                />
                <Chip
                  label={`Team: ${company.status_by_team || "Active"}`}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    borderWidth: 1,
                    ...statusStyle(company.status_by_team),
                  }}
                />
                <Chip
                  label={`System: ${company.status_by_system || "Active"}`}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    borderWidth: 1,
                    ...statusStyle(company.status_by_system),
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => {
                setActiveTab(val);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTab-root": {
                  width: 120,
                  minWidth: 120,
                },
              }}
            >
              <Tab label="Orders" />
              <Tab label="Invoice" />
              <Tab label="Payment" />
              <Tab label="Report" />
              <Tab label="Team Report" />
              <Tab label="Checklist" />
              <Tab label="Visits" />
              <Tab label="Messages"/>
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Box>
              {years.map((block) => {
                const balance =
                  (block.orders_total || 0) - (block.payments_total || 0);

                return (
                  <Accordion
                    key={block.year?.id}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        background:
                          "linear-gradient(to right, #f1f5f9, #e0f2fe)",
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography fontWeight={700}>
                          {block.year?.name}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              balance >= 0 ? "#16a34a" : "#dc2626",
                            fontWeight: 600,
                          }}
                        >
                          Balance: ₹ {fmtRs(balance)}
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ background: "#fff" }}>
                      {!block.has_setup ? (
                        <Typography color="text.secondary">
                          No billing setup available
                        </Typography>
                      ) : (
                        <>
                          {/* 💰 SUMMARY */}
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                              <SummaryCard
                                title="Orders"
                                value={block.orders_total}
                                color="linear-gradient(135deg,#6366f1,#4f46e5)"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <SummaryCard
                                title="Payments"
                                value={block.payments_total}
                                color="linear-gradient(135deg,#22c55e,#16a34a)"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <SummaryCard
                                title="Balance"
                                value={balance}
                                color={
                                  balance >= 0
                                    ? "linear-gradient(135deg,#0ea5e9,#0284c7)"
                                    : "linear-gradient(135deg,#ef4444,#dc2626)"
                                }
                              />
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          {/* 🧾 SERVICES */}
                          <Typography fontWeight={600} sx={{ mb: 1 }}>
                            Services
                          </Typography>

                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: "#f1f5f9" }}>
                                <TableCell>Service</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(block.service_orders || []).length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    No orders found
                                  </TableCell>
                                </TableRow>
                              ) : (
                                block.service_orders.map((o, i) => (
                                  <TableRow
                                    key={o.id}
                                    hover
                                    sx={{
                                      background:
                                        i % 2 === 0
                                          ? "#ffffff"
                                          : "#f9fafb",
                                    }}
                                  >
                                    <TableCell>{o.service_name}</TableCell>
                                    <TableCell>{o.service_plan_name}</TableCell>
                                    <TableCell align="right">
                                      ₹ {fmtRs(o.total_amount)}
                                    </TableCell>
                                    <TableCell>{o.order_date || "—"}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>

                          <Divider sx={{ my: 3 }} />

                          {/* 💳 PAYMENTS */}
                          <Typography fontWeight={600} sx={{ mb: 1 }}>
                            Payments
                          </Typography>

                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: "#f1f5f9" }}>
                                <TableCell>Date</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Reference</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(block.payments || []).length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    No payments found
                                  </TableCell>
                                </TableRow>
                              ) : (
                                block.payments.map((p, i) => (
                                  <TableRow
                                    key={p.id}
                                    hover
                                    sx={{
                                      background:
                                        i % 2 === 0
                                          ? "#ffffff"
                                          : "#f9fafb",
                                    }}
                                  >
                                    <TableCell>{p.payment_date || "—"}</TableCell>
                                    <TableCell>{p.method}</TableCell>
                                    <TableCell align="right">
                                      ₹ {fmtRs(p.amount)}
                                    </TableCell>
                                    <TableCell>{p.reference}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {years.map((block) => {
                const invoices = (block.issued_invoices || []).filter((inv) => {
                  const s = String(inv.status || "").toUpperCase();
                  return ["ISSUED", "INVOICED", "PARTIAL_PAID", "PAID"].includes(s);
                });
                return (
                  <Accordion
                    key={`inv-${block.year?.id}`}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        background:
                          "linear-gradient(to right, #f1f5f9, #e0f2fe)",
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography fontWeight={700}>
                          {block.year?.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Issued: ₹ {fmtRs(block.issued_total)} · Paid: ₹{" "}
                          {fmtRs(block.paid_invoice_total)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ background: "#fff" }}>
                      {!block.has_setup ? (
                        <Typography color="text.secondary">
                          No billing setup available
                        </Typography>
                      ) : (
                        <>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                              <SummaryCard
                                title="Issued"
                                value={block.issued_total}
                                color="linear-gradient(135deg,#3b82f6,#2563eb)"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <SummaryCard
                                title="Paid"
                                value={block.paid_invoice_total}
                                color="linear-gradient(135deg,#22c55e,#16a34a)"
                              />
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          <Typography fontWeight={600} sx={{ mb: 1 }}>
                            Invoices
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: "#f1f5f9" }}>
                                <TableCell>Invoice #</TableCell>
                                <TableCell>Issue Date</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="center" width={90}>
                                  Print
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {invoices.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={6}>
                                    No issued or paid invoices found
                                  </TableCell>
                                </TableRow>
                              ) : (
                                invoices.map((inv, i) => (
                                  <TableRow
                                    key={inv.id}
                                    hover
                                    sx={{
                                      cursor: "pointer",
                                      background:
                                        i % 2 === 0 ? "#ffffff" : "#f9fafb",
                                    }}
                                    onClick={() => {
                                      if (inv.billing_config && inv.id) {
                                        navigate(
                                          `/billing/issue-invoice/?config_id=${inv.billing_config}&issue_invoice_id=${inv.id}`
                                        );
                                      }
                                    }}
                                  >
                                    <TableCell>
                                      {inv.invoice_number || "—"}
                                    </TableCell>
                                    <TableCell>
                                      {inv.issue_date || "—"}
                                    </TableCell>
                                    <TableCell>
                                      {inv.due_date || "—"}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={formatInvoiceStatus(inv.status)}
                                        sx={{
                                          fontWeight: 600,
                                          ...invoiceStatusChipSx(inv.status),
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      ₹ {fmtRs(inv.total_amount)}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Print invoice">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(e) => handlePrintInvoice(inv, e)}
                                        >
                                          <PrintOutlinedIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Payments by academic year
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={() => openCreateDialog("payment")}
                >
                  Add Payment
                </Button>
              </Box>
              {years.map((block) => {
                const payments = block.payments || [];
                return (
                  <Accordion
                    key={`pay-${block.year?.id}`}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        background:
                          "linear-gradient(to right, #f1f5f9, #e0f2fe)",
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography fontWeight={700}>
                          {block.year?.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Payments: ₹ {fmtRs(block.payments_total)} ·{" "}
                          {payments.length} record{payments.length === 1 ? "" : "s"}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ background: "#fff" }}>
                      {!block.has_setup ? (
                        <Typography color="text.secondary">
                          No billing setup available
                        </Typography>
                      ) : (
                        <>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                              <SummaryCard
                                title="Total Payments"
                                value={block.payments_total}
                                color="linear-gradient(135deg,#22c55e,#16a34a)"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Card
                                sx={{
                                  borderRadius: 3,
                                  color: "#fff",
                                  background: "linear-gradient(135deg,#0ea5e9,#0284c7)",
                                  boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                                }}
                              >
                                <CardContent>
                                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    Payment records
                                  </Typography>
                                  <Typography variant="h5" fontWeight={700}>
                                    {payments.length}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          <Typography fontWeight={600} sx={{ mb: 1 }}>
                            Payment details
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: "#f1f5f9" }}>
                                <TableCell>Date</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {payments.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={6}>
                                    No payments found
                                  </TableCell>
                                </TableRow>
                              ) : (
                                payments.map((p, i) => (
                                  <TableRow
                                    key={p.id}
                                    hover
                                    sx={{
                                      cursor: "pointer",
                                      background:
                                        i % 2 === 0 ? "#ffffff" : "#f9fafb",
                                    }}
                                    onClick={() => {
                                      if (block.year?.id && p.id) {
                                        navigate(
                                          `/billing/payment?year=${block.year.id}&company=${companyId}&payment_id=${p.id}`
                                        );
                                      }
                                    }}
                                  >
                                    <TableCell>
                                      {p.payment_date
                                        ? String(p.payment_date).slice(0, 10)
                                        : "—"}
                                    </TableCell>
                                    <TableCell>{p.method || "—"}</TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={p.status || "—"}
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </TableCell>
                                    <TableCell>{p.reference || "—"}</TableCell>
                                    <TableCell>{p.notes || "—"}</TableCell>
                                    <TableCell align="right">
                                      ₹ {fmtRs(p.amount)}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}

          {activeTab === 3 && (
            <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "#f1f5f9" }}>
                      <TableCell width={48} />
                      <TableCell sx={{ fontWeight: 600 }}>Academic Year</TableCell>
                      {summaryColumns.map((col) => (
                        <TableCell key={col.schemacolumn} sx={{ fontWeight: 600 }}>
                          {col.column}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={summaryColumns.length + 2}>
                          <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No report data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      [...reports].reverse().map((row) => {
                        const isExpanded = expandedRowId === row.id;
                        return (
                          <React.Fragment key={row.id}>
                            <TableRow hover>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                                >
                                  {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{formatValue(row.tab_name)}</TableCell>
                              {summaryColumns.map((col) => (
                                <TableCell key={col.schemacolumn}>
                                  {formatValue(row[col.schemacolumn])}
                                </TableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ py: 0 }} colSpan={summaryColumns.length + 2}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, backgroundColor: '#fbfdff' }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                      More Report Data
                                    </Typography>
                                    <Grid container spacing={2}>
                                      {detailColumns.map((col) => (
                                        <Grid item xs={12} md={4} key={col.schemacolumn}>
                                          <Box sx={{ borderBottom: '1px solid #eef2f7', pb: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                              {col.column}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                              {formatValue(row[col.schemacolumn])}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {activeTab === 4 && (
            <Box>
              {teamReports.length === 0 ? (
                <Typography color="text.secondary">No team reports found.</Typography>
              ) : (
                teamReports.map((report) => (
                  <Card key={report.id} sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Submitted by: {report.created_by_name || "Unknown"} on {new Date(report.created).toLocaleString()}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Parameter</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.param_updates?.map((paramUpdate) => (
                            <TableRow key={paramUpdate.id}>
                              <TableCell>{paramUpdate.param_name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={paramUpdate.is_using ? "Using" : "Not Using"}
                                  size="small"
                                  color={paramUpdate.is_using ? "success" : "error"}
                                />
                              </TableCell>
                              <TableCell>{paramUpdate.reason || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
          {/* ─── VISIT SUMMARY TAB ─── */}
          {activeTab === 6 && (
            <VisitSummaryTab companyId={companyId} />
          )}
          {activeTab === 5 && (
            <Box>
              <CompanyChecklistTab company_id={companyId} company={company} />
            </Box>
          )}

          {activeTab === 7 && (
            <Box sx={{ p: 2}}>
              <Card variant="outlined" sx={{ mb: 3}}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3}}>
                    Last Synced Report Data
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#f8fafc" }} elevation={0}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                          {reports.length > 0 ? (reports[0]?.smsTotal || 0) : 0}
                          {/* {companyResources ? (companyResources[0]?.rows?.find(r => r.name === 'sms')?.max_limit || 0) : 0} */}
                        </Typography>
                        {/* <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all'}}>
                          {JSON.stringify(reports[0])}
                        </Typography> */}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8fafc' }} elevation={0}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Balance
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ mt: 1, fontWeight: 'bold'}}>
                          {reports.length > 0 ? (reports[0]?.sms_left || 0) : 0}
                          {/* {companyResources ? (companyResources[0]?.rows?.find(r => r.name === 'sms')?.available || 0) : 0} */}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p:3, textAlign: 'center', bgcolor: '#f8fafc'}} elevation={0}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Pending
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ mt: 1, fontWeight: 'bold'}}>
                          {reports.length > 0 ? (reports[0]?.sms_used || 0) : 0}
                          {/* {companyResources ? (companyResources[0]?.rows?.find(r => r.name === 'sms')?.usage || 0) : 0} */}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          <Dialog
            open={createDialogOpen}
            onClose={() => !createDialogLoading && setCreateDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Company: <strong>{company?.name}</strong>
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={createDialogYear}
                  label="Academic Year"
                  onChange={(e) => {
                    setCreateDialogYear(e.target.value);
                    resetSetupFields();
                  }}
                >
                  {years.map((block) => (
                    <MenuItem key={block.year?.id} value={block.year?.id}>
                      {block.year?.name}
                      {block.has_setup ? "" : " (no setup)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {needsBillingSetup && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    No billing setup exists for this year. Enter details below to create
                    one and continue to order creation.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student Strength"
                    type="number"
                    required
                    value={setupStudentStrength}
                    onChange={(e) => {
                      setSetupStudentStrength(e.target.value);
                      setSetupStudentStrengthError("");
                    }}
                    inputProps={{ min: 1, max: 9999, step: 1 }}
                    error={!!setupStudentStrengthError}
                    helperText={setupStudentStrengthError}
                    sx={{ mb: 2 }}
                  />
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    error={!!setupCoordinatorError}
                  >
                    <InputLabel>Coordinator</InputLabel>
                    <Select
                      value={setupCoordinator}
                      label="Coordinator"
                      onChange={(e) => {
                        setSetupCoordinator(e.target.value);
                        setSetupCoordinatorError("");
                      }}
                    >
                      {coordinators.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.username || u.email || u.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {setupCoordinatorError && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {setupCoordinatorError}
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCreateDialogOpen(false)}
                disabled={createDialogLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateDialogContinue}
                disabled={createDialogLoading || !createDialogYear}
              >
                {dialogContinueLabel}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={downloadDialogOpen}
            onClose={handleDownloadDialogClose}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600 }}>Print invoice PDF</DialogTitle>
            <DialogContent>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Show GST</InputLabel>
                <Select
                  value={downloadShowGst ? "yes" : "no"}
                  label="Show GST"
                  onChange={(e) => setDownloadShowGst(e.target.value === "yes")}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Show Discount</InputLabel>
                <Select
                  value={downloadShowDiscount ? "yes" : "no"}
                  label="Show Discount"
                  onChange={(e) => setDownloadShowDiscount(e.target.value === "yes")}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Add Billing Summary</InputLabel>
                <Select
                  value={downloadShowBillingSummary ? "yes" : "no"}
                  label="Add Billing Summary"
                  onChange={(e) =>
                    setDownloadShowBillingSummary(e.target.value === "yes")
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              {downloadShowBillingSummary ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  sx={{ mt: 2 }}
                  label="Billing Summary"
                  value={downloadBillingSummary}
                  onChange={(e) => setDownloadBillingSummary(e.target.value)}
                  placeholder="Type billing summary text to include in PDF..."
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDownloadDialogClose} disabled={downloadLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadInvoiceSubmit}
                disabled={downloadLoading}
              >
                {downloadLoading ? "Preparing…" : "Print / Download"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}